import { useMemo } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Dotdotdot } from '@hivebrite/design-system/Dotdotdot';
import { StaticGoogleMap } from '@hivebrite/design-system/StaticGoogleMap';

import { useLandingContext } from 'hb-react/frontoffice/apps/LiveFeed/contexts/LandingContext';
import MapPreview from 'hb-react/shared/components/MapPreview';
import useFrameTheme from 'hb-react/shared/hooks/theme/useFrameTheme';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import IconLocation from 'hb-react/shared/icons/Location';
import dayjs from 'hb-react/shared/utils/dayjs/dayjs';
import ThemeProvider from 'hb-react/style/ThemeProvider';

import Landing from '../Landing';

const transportationNames = {
  plane: s__('TripPost|plane'),
  train: s__('TripPost|train'),
  subway: s__('TripPost|subway'),
  bike: s__('TripPost|bike'),
  feet: s__('TripPost|feet'),
  car: s__('TripPost|car'),
  other: s__('TripPost|other'),
};

const Trip = () => {
  const { flagGoogleMapsComponent } = useFeatureFlag();
  const {
    arrivalLocation,
    departureLocation,
    endDate,
    startDate,
    transportation,
    timezone,
  } = useLandingContext();
  const frameTheme = useFrameTheme();

  const getIconProperties = () => {
    switch (transportation) {
      case 'plane':
        return { iconName: 'plane', rotation: -30 };
      case 'train':
        return { iconName: 'train', rotation: 0 };
      case 'subway':
        return { iconName: 'train-subway-tunnel', rotation: 0 };
      case 'bike':
        return { iconName: 'bicycle', rotation: 0 };
      case 'feet':
        return { iconName: 'person-walking', rotation: 0 };
      case 'car':
        return { iconName: 'car', rotation: 0 };
      case 'other':
        return { iconName: 'ufo', rotation: 0 };
      default:
        return { iconName: 'plane', rotation: -30 };
    }
  };
  const { iconName, rotation } = getIconProperties();

  const transportationLabel = useMemo(
    () => transportationNames[transportation],
    [transportation],
  );
  const title = useMemo(() => {
    if (departureLocation?.city && arrivalLocation?.city) {
      return s__(
        'LiveFeed|is travelling from %{departure} to %{arrival} by %{transportation}',
      ).i__({
        arrival: arrivalLocation?.city,
        departure: departureLocation?.city,
        transportation: transportationLabel,
      });
    }

    if (departureLocation?.city) {
      return s__(
        'LiveFeed|is travelling from %{departure} by %{transportation}',
      ).i__({
        departure: departureLocation?.city,
        transportation: transportationLabel,
      });
    }

    return s__('LiveFeed|is travelling to %{arrival} by %{transportation}').i__(
      {
        arrival: arrivalLocation?.city || arrivalLocation.address,
        transportation: transportationLabel,
      },
    );
  }, [departureLocation, arrivalLocation, transportationLabel]);

  const customMx = flagGoogleMapsComponent ? 0 : { _: -4, m: 0 };

  return (
    <Landing title={title}>
      <Box mt={4} mx={customMx} display="block" css="position: relative;">
        {flagGoogleMapsComponent ? (
          <StaticGoogleMap
            apiKey={window.__HB_ENV__.GOOGLEMAPS_API_KEY}
            errorMessage={__('StaticGoogleMap|Error loading map')}
            footer={{
              iconName,
              address: arrivalLocation?.address,
              rotation,
              dateRange: [
                timezone
                  ? dayjs(startDate).tz(timezone).format('ll')
                  : dayjs(startDate).format('ll'),
                timezone
                  ? dayjs(endDate).tz(timezone).format('ll')
                  : dayjs(endDate).format('ll'),
              ],
            }}
            latitude={arrivalLocation?.lat}
            longitude={arrivalLocation?.lng}
            zoom={13}
          />
        ) : (
          <>
            <MapPreview
              lat={arrivalLocation?.lat}
              lng={arrivalLocation?.lng}
              width="100%"
              height={200}
              zoom={4}
            />
            <ThemeProvider
              theme={frameTheme.themeName}
              fontBrightness={frameTheme.fontBrightness}
            >
              <Box
                bg={frameTheme.backgroundColor}
                p={3}
                pr={5}
                alignItems="center"
                css="position: absolute; bottom: 0; left: 0;"
              >
                <Box color="primary" mr={3}>
                  <IconLocation height={30} width={30} />
                </Box>
                <Box display="block">
                  <Box fontWeight={1} mb={1}>
                    <Dotdotdot clamp={1}>{arrivalLocation?.address}</Dotdotdot>
                  </Box>
                  <Box>
                    {timezone
                      ? dayjs(startDate).tz(timezone).format('ll')
                      : dayjs(startDate).format('ll')}{' '}
                    -{' '}
                    {timezone
                      ? dayjs(endDate).tz(timezone).format('ll')
                      : dayjs(endDate).format('ll')}
                  </Box>
                </Box>
              </Box>
            </ThemeProvider>
          </>
        )}
      </Box>
    </Landing>
  );
};

export default Trip;
